import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUpgrade } from "@/context/UpgradeContext";
import { Terminal, LayoutDashboard, FileText, Store, Mail, History as HistoryIcon, CreditCard, Settings, LogOut, Lock, ChevronsLeft, ChevronsRight } from "lucide-react";

const tools = [
  { to: "/patch-notes", label: "Steam Patch Notes", tag: "Tool 01", icon: FileText, testid: "nav-patch-notes", free: true },
  { to: "/store-copy", label: "Steam Store Page", tag: "Tool 02", icon: Store, testid: "nav-store-copy", free: false },
  { to: "/cold-email", label: "Streamer & PR Pitch", tag: "Tool 03", icon: Mail, testid: "nav-cold-email", free: false },
];

const navItemClass = ({ isActive }) =>
  `group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-200 ${
    isActive
      ? "bg-teal-400/10 text-teal-300 ring-1 ring-teal-500/30"
      : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
  }`;

const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const { user, logout } = useAuth();
  const { openUpgrade } = useUpgrade();
  const isPro = !!user?.is_pro;
  const remaining = Math.max(0, (user?.free_limit ?? 3) - (user?.generations_used ?? 0));

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-zinc-800 bg-zinc-950 w-64 transition-[transform,width] duration-200 ${
        collapsed ? "lg:w-[72px]" : "lg:w-64"
      } ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="flex items-center gap-2 h-16 px-4 border-b border-zinc-800">
        <div className="h-9 w-9 shrink-0 flex items-center justify-center bg-teal-400 rounded-xl shadow-[0_0_18px_rgba(45,212,191,0.35)]">
          <Terminal className="h-5 w-5 text-zinc-950" />
        </div>
        {!collapsed && (
          <span className="font-heading font-black tracking-tighter text-zinc-100 whitespace-nowrap">
            IndieLaunchkit
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto" onClick={() => setMobileOpen(false)}>
        <NavLink to="/" end data-testid="nav-dashboard" className={navItemClass} title="Dashboard">
          <LayoutDashboard className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Dashboard</span>}
        </NavLink>

        {!collapsed && (
          <p className="px-2 pb-2 pt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            Tools
          </p>
        )}
        {tools.map((t) => {
          const locked = !isPro && !t.free;
          if (locked) {
            return (
              <button
                key={t.to}
                onClick={() => openUpgrade(`${t.label} is a Pro tool. Upgrade to unlock it.`)}
                data-testid={`${t.testid}-locked`}
                title={`${t.label} (Pro)`}
                className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl text-zinc-500 hover:bg-zinc-900 transition-colors duration-200"
              >
                <t.icon className="h-5 w-5 shrink-0" />
                {!collapsed && (
                  <span className="flex flex-col leading-tight min-w-0 flex-1 text-left">
                    <span className="text-sm font-medium truncate">{t.label}</span>
                    <span className="text-[10px] text-zinc-600 font-mono">{t.tag}</span>
                  </span>
                )}
                {!collapsed && (
                  <span className="flex items-center gap-1 shrink-0 text-[9px] font-mono font-bold uppercase tracking-wider text-teal-300 bg-teal-400/10 border border-teal-500/30 rounded-md px-1.5 py-0.5" data-testid={`${t.testid}-pro-badge`}>
                    <Lock className="h-2.5 w-2.5" /> Pro
                  </span>
                )}
              </button>
            );
          }
          return (
            <NavLink key={t.to} to={t.to} data-testid={t.testid} className={navItemClass} title={t.label}>
              <t.icon className="h-5 w-5 shrink-0" />
              {!collapsed && (
                <span className="flex flex-col leading-tight min-w-0">
                  <span className="text-sm font-medium truncate">{t.label}</span>
                  <span className="text-[10px] text-zinc-600 font-mono">{t.tag}</span>
                </span>
              )}
            </NavLink>
          );
        })}

        <NavLink to="/history" data-testid="nav-history" className={navItemClass} title="History">
          <HistoryIcon className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="text-sm font-medium">History</span>}
        </NavLink>

        <NavLink to="/pricing" data-testid="nav-pricing" className={navItemClass} title="Pricing">
          <CreditCard className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Pricing</span>}
        </NavLink>

        <div className="pt-2">
          <NavLink to="/settings" data-testid="nav-settings" className={navItemClass} title="Settings">
            <Settings className="h-5 w-5 shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Settings</span>}
          </NavLink>
        </div>
      </nav>

      {/* Free generations counter */}
      {!isPro && !collapsed && (
        <div className="mx-3 mb-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3" data-testid="free-usage-counter">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Free Generations</span>
            <span className="font-mono text-[10px] text-teal-300" data-testid="free-usage-text">
              {remaining}/{user?.free_limit ?? 3} left
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="h-full bg-teal-400 transition-[width] duration-300"
              style={{ width: `${((user?.free_limit ?? 3) - remaining) / (user?.free_limit ?? 3) * 100}%` }}
            />
          </div>
          <button
            onClick={() => openUpgrade("Get unlimited generations and unlock every tool.")}
            data-testid="counter-upgrade-btn"
            className="mt-3 w-full text-center text-[11px] font-semibold text-zinc-950 bg-teal-400 hover:bg-teal-300 rounded-lg py-1.5 transition-colors duration-200"
          >
            Upgrade to Pro
          </button>
        </div>
      )}

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        data-testid="sidebar-collapse-btn"
        className="mx-3 mb-2 hidden lg:flex items-center gap-2 px-3 py-2 rounded-xl text-zinc-500 hover:text-teal-300 hover:bg-zinc-900 transition-colors duration-200 text-sm"
      >
        {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        {!collapsed && <span className="font-mono text-xs">Collapse</span>}
      </button>

      {/* User profile box */}
      <div className="border-t border-zinc-800 p-3" data-testid="sidebar-user-box">
        <div className={`flex items-center gap-3 rounded-xl ${collapsed ? "justify-center" : "bg-zinc-900/60 p-2"}`}>
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-9 w-9 rounded-xl object-cover border border-zinc-700" />
          ) : (
            <div className="h-9 w-9 rounded-xl bg-zinc-800 flex items-center justify-center text-teal-300 font-heading font-bold">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm text-zinc-100 truncate font-medium">{user?.name || "Developer"}</p>
              <p className="text-[11px] text-zinc-500 truncate font-mono">{user?.email}</p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={logout}
              data-testid="sidebar-logout-btn"
              className="text-zinc-500 hover:text-teal-300 transition-colors duration-200"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
